import styled from '@emotion/styled';
import { Constants, Theme } from '@seedlang/constants';
import { CaseFillInTheBlanksCardWrapper } from '@seedlang/hoc';
import { DeckUI, OverlayUI } from '@seedlang/state';
import { flexCenterColumn } from '@seedlang/style_mixins';
import { isPresent } from '@seedlang/utils';
import CardArrowBox from 'components/card/card_arrow_box';
import EvaluateButtons from 'components/card/evaluate_buttons';
import MultipleChoiceButtons from 'components/card/multiple_choice_buttons';
import GrammarHint from 'components/grammar_hint';
import CardContent from 'components/layout/card_content';
import CardAnswerIndicator from 'components/card/card_answer_indicator';
import CardWrapper from 'components/layout/card_wrapper';
import SentenceBreakdown from 'components/sentence_breakdown';
import Spinner from 'components/spinner';
import Text from 'components/text';
import TextResizer from 'components/text_resizer';
import CardSection from 'components/vocab_card/card_section';
import MediaSection from 'components/vocab_card/media_section';
import { observer } from 'mobx-react';
import React from 'react';

const SentenceWrapper = styled.div`
  ${flexCenterColumn()}
  flex: 1;
  width: 100%;
  height: 80%;
`;

@observer
class CaseFillInTheBlanksCardMultipleChoice extends React.Component {
  openSourceEdit = () => {
    DeckUI.createEvent('deck - open translation report screen', {
      language_id: this.props.card?.languageId || DeckUI.deck?.languageId,
      card_type_id: this.props.card?.cardType?.id || this.props.card?.cardTypeId,
    });
    OverlayUI.addOverlay('sourceEdit');
  }

  render() {
    return (
      <CardWrapper
        card={this.props.card}
        cardState={this.props.cardState}
        userCard={this.props.userCard}
        icons={this.props.icons}
      >
        <CardContent
          height={this.props.cardState.revealed ?
            DeckUI.layout.contentHeight :
            DeckUI.layout.contentHeightWithMultipleChoiceButtons(this.props.wordFormOptions?.length || 4)
          }
        >
          {
            this.props.cardState.revealed &&
            <MediaSection
              showMedia
              autoPlay={this.props.inView}
              card={this.props.card}
              cardState={this.props.cardState}
              height={this.props.sectionHeight}
              borderTopRadius={10}
            />
          }
          <CardSection
            background='#FFF'
            padding='20px'
            numSections={this.props.numSections}
            borderTopRadius={this.props.cardState.revealed ? 0 : 10}
            height={this.props.sectionHeight}
          >
            <TextResizer
              resizeText
              height={this.props.sectionHeight - 20}
              width={DeckUI.layout.innerDeckWidth - 40}
              characterLength={this.props.topSectionText && this.props.topSectionText.length}
            >
              <Text
                rtl={Constants.RTL_LANGUAGE_IDS.includes(this.props.topSectionLanguageId)}
                cursor={this.props.cardState.revealed ? 'pointer' : null}
                onClick={this.props.cardState.revealed ? this.openSourceEdit : null}
              >
                {this.props.topSectionText}
              </Text>
            </TextResizer>
          </CardSection>
          <CardSection
            numSections={this.props.numSections}
            padding='20px'
            background='#CCC'
            borderBottomRadius={10}
            height={this.props.sectionHeight}
          >
            {
              this.props.cardState.revealed &&
              <CardAnswerIndicator
                correct={this.props.userCard.correct}
                userAnswer={this.props.selectedWordForm}
                zIndex={Theme.z['card-4']}
              />
            }
            {
              this.props.grammarHint &&
              <GrammarHint
                description={this.props.grammarHint.description}
                backgroundColor={this.props.grammarHint.color}
              />
            }
            <SentenceWrapper>
              <TextResizer
                resizeText
                maximumFontSize={30}
                height={this.props.cardState.revealed ? this.props.sectionHeight - 80 : this.props.sectionHeight - 20}
                width={DeckUI.layout.deckWidth - 40}
                characterLength={this.props.card.sentence && this.props.card.sentence.targetText && this.props.card.sentence.targetText.length}
              >
                <SentenceBreakdown
                  center
                  showGapUnderline={!this.props.cardState.revealed}
                  showUnderline={this.props.cardState.revealed}
                  showCase={this.props.cardState.revealed}
                  inputWordAssociationIds={this.props.cardState.revealed ? [] : this.props.card.wordAssociationIds}
                  highlightWordAssociationIds={this.props.cardState.revealed ? this.props.card.wordAssociationIds : []}
                  highlightType='background'
                  sentence={this.props.card.sentence}
                  disableOnClick={!this.props.cardState.revealed}
                  defaultBorderColor="#b0b0b0"
                />
              </TextResizer>
              {
                this.props.card.sentence && !this.props.card.sentence.enrichedWords &&
                <Spinner
                  width='40px'
                  height='12px'
                  margin='10px auto 0'
                  background='#999'
                />
              }
            </SentenceWrapper>
          </CardSection>
          <CardArrowBox
            card={this.props.card}
            currentArrowBox={this.props.currentArrowBox}
            possibleArrowBoxes={this.props.possibleArrowBoxes}
          />
        </CardContent>
        {
          !this.props.cardState.revealed && isPresent(this.props.wordFormOptions) &&
          <MultipleChoiceButtons
            options={this.props.wordFormOptions}
            selected={this.props.selectedWordForm ? [this.props.selectedWordForm] : []}
            onClick={this.props.onSelectWordForm}
            disabled={!this.props.inView}
            field='label'
            languageId='DE'
            revealed={this.props.cardState.revealed}
            correct={this.props.userCard.correct}
            width={DeckUI.layout.innerDeckWidth}
            height={DeckUI.layout.totalMultipleChoiceButtonHeight(this.props.wordFormOptions?.length || 4)}
            buttonHeight={DeckUI.layout.multipleChoiceButtonHeight}
            fontSize='16px'
            isMobile={DeckUI.layout.isMobile}
          />
        }
        {
          !this.props.cardState.revealed && !isPresent(this.props.wordFormOptions) &&
          <Spinner />
        }
        {
          this.props.cardState.revealed &&
          <EvaluateButtons
            card={this.props.card}
            cardState={this.props.cardState}
            userCard={this.props.userCard}
            disabled={!this.props.inView}
            onNextClick={this.props.onNextClick}
            onCheckAnswer={this.props.onCheckAnswer}
          />
        }
      </CardWrapper>
    );
  }
}

export default CaseFillInTheBlanksCardWrapper(CaseFillInTheBlanksCardMultipleChoice);